<script setup lang="ts">
import { just, nothing } from "../lib/maybe";
import { nextTick, useTemplateRef } from "vue";
import { uniqueDictionaryWords } from "../lib/projectDictionary";
import ZwButton from "./ui/ZwButton.vue";
import ZwInput from "./ui/ZwInput.vue";

const dictionary = defineModel<string[]>("dictionary", { required: true });
const addDraft = defineModel<string>("addDraft", { required: true });

const dictionaryAddInput =
  useTemplateRef<InstanceType<typeof ZwInput>>("dictionaryAddInput");

function sortDictionaryWords(words: string[]): string[] {
  return uniqueDictionaryWords(words).sort((a, b) =>
    a.localeCompare(b, "en", { sensitivity: "base" }),
  );
}

function addDictionaryWord(): void {
  const word = addDraft.value.trim();
  if (word.length === 0) return;
  const key = word.toLocaleLowerCase();
  if (dictionary.value.some((entry) => entry.toLocaleLowerCase() === key)) {
    addDraft.value = "";
    return;
  }
  dictionary.value = sortDictionaryWords([...dictionary.value, word]);
  addDraft.value = "";
  void nextTick(() => dictionaryAddInput.value?.focus());
}

function updateDictionaryWord(index: number, value: string): void {
  const next = [...dictionary.value];
  next[index] = value;
  dictionary.value = next;
}

function removeDictionaryWord(index: number): void {
  dictionary.value = dictionary.value.filter((_, i) => i !== index);
}

defineExpose({
  focus: () => dictionaryAddInput.value?.focus(),
});
</script>

<template>
  <section class="project-settings-group" aria-label="Dictionary">
    <h3 class="project-settings-subhead">Dictionary</h3>
    <p class="project-settings-hint">
      Words to accept when checking spelling. Worldbuilding names are included
      automatically.
    </p>

    <div class="project-settings-dictionary-add">
      <ZwInput
        :id="nothing"
        ref="dictionaryAddInput"
        v-model="addDraft"
        :label="just('Add word')"
        placeholder="Word"
        autocomplete="off"
        spellcheck="false"
      />
      <ZwButton
        size="sm"
        variant="primary"
        class="project-settings-dictionary-add-btn"
        :disabled="addDraft.trim().length === 0"
        @click="addDictionaryWord"
      >
        Add
      </ZwButton>
    </div>

    <div class="project-settings-dictionary-list">
      <div
        v-for="(word, index) in dictionary"
        :key="index"
        class="project-settings-dictionary-row"
      >
        <ZwInput
          :id="nothing"
          :model-value="word"
          :aria-label="`Dictionary word ${index + 1}`"
          autocomplete="off"
          spellcheck="false"
          :label="nothing"
          @update:model-value="updateDictionaryWord(index, $event)"
        />
        <ZwButton
          size="sm"
          variant="danger"
          class="project-settings-dictionary-remove"
          @click="removeDictionaryWord(index)"
        >
          Remove
        </ZwButton>
      </div>
      <p v-if="dictionary.length === 0" class="project-settings-hint">
        No custom words yet.
      </p>
    </div>
  </section>
</template>
